import { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { supabase } from '../supabase/supabaseClient';
import { useAuth } from './auth.context';
import { useProfile } from './profile.context';
import { ASSET_CLASSES, LIABILITY_CLASSES, DB, YEAR_OPTIONS } from 'utilities/global.variables';

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const { targetNetworth } = useProfile();
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [years, setYears] = useState(20);

  useEffect(() => {
    if (user) {
      fetchAllData();
    } else {
      setAssets([]);
      setLiabilities([]);
      setLoadingUserData(false);
    }
  }, [user]);


  // FETCH ALL ASSETS AND LIABILITIES

  const fetchAllData = async () => {
    setLoadingUserData(true);
    setDataError(null);
    await Promise.all([fetchAssets(), fetchLiabilities()]);
    await Promise.all([fetchAssetsHistory(), fetchLiabilitiesHistory()]);
    setLoadingUserData(false);
  };


  // FETCH ALL ASSETS

  const fetchAssets = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from(DB.ASSETS)
      .select('id, name, description, valuation, ownership, classification, updated, user_id, include_networth,annual_contributions, annual_growth')
      .eq('user_id', user.id);
    if (error) { console.error('Error fetching assets:', error); setDataError('Failed to load your assets. Please refresh the page.'); return; }
    if (data) setAssets(data);
  };


  // FETCH ALL LIABILITIES

  const fetchLiabilities = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from(DB.LIABILITIES)
      .select('id, name, description, valuation, ownership, classification, updated, user_id, include_networth, interest_rate, term_years, term_months, being_repaid')
      .eq('user_id', user.id);
    if (error) { console.error('Error fetching liabilities:', error); setDataError('Failed to load your liabilities. Please refresh the page.'); return; }
    if (data) setLiabilities(data);
  };


  // CREATE ASSET

  const createAsset = async (asset) => {
    if (!user) return;
    const { data, error } = await supabase
      .from(DB.ASSETS)
      .insert({
        name: asset.name,
        description: asset.description,
        valuation: asset.valuation,
        ownership: asset.ownership,
        classification: asset.classification,
        updated: new Date().toISOString(),
        include_networth: asset.include_networth,
        annual_contributions: asset.annual_contributions,
        annual_growth: asset.annual_growth,
        user_id: user.id,
      })
      .select();
    if (error) { console.error('Error creating asset:', error); return { error }; }
    if (data && data[0] && asset.history) await syncHistory(DB.ASSETS_HISTORY, DB.ASSET_ID, data[0].id, asset.history);
    await fetchAssets();
    await fetchAssetsHistory();
    return { data };
  };


  // UPDATE ASSET

  const updateAsset = async (updated_asset, id) => {
    const { data, error } = await supabase
      .from(DB.ASSETS)
      .update({
        name: updated_asset.name,
        description: updated_asset.description,
        valuation: updated_asset.valuation,
        ownership: updated_asset.ownership,
        classification: updated_asset.classification,
        updated: new Date().toISOString(),
        include_networth: updated_asset.include_networth,
        annual_contributions: updated_asset.annual_contributions,
        annual_growth: updated_asset.annual_growth,
      })
      .eq('id', id)
      .select();
    if (error) { console.error('Error updating asset:', error); return { error }; }
    if (updated_asset.history) await syncHistory(DB.ASSETS_HISTORY, DB.ASSET_ID, id, updated_asset.history);
    fetchAssets().then(() => fetchAssetsHistory());
    return { data };
  };


  // DELETE ASSET

  const deleteAsset = async (id) => {
    await supabase.from(DB.ASSETS_HISTORY).delete().eq(DB.ASSET_ID, id).eq('user_id', user.id);
    const { data, error } = await supabase.from(DB.ASSETS).delete().eq('id', id).eq('user_id', user.id).select();
    if (error) { console.error('Error deleting asset:', error); return { error }; }
    await fetchAssets();
    return { data };
  };


  // CREATE LIABILITY

  const createLiability = async (liability) => {
    if (!user) return;
    const { data, error } = await supabase
      .from(DB.LIABILITIES)
      .insert({
        name: liability.name,
        description: liability.description,
        valuation: liability.valuation,
        ownership: liability.ownership,
        classification: liability.classification,
        updated: new Date().toISOString(),
        include_networth: liability.include_networth,
        interest_rate: liability.interest_rate,
        term_years: liability.term_years,
        term_months: liability.term_months,
        being_repaid: liability.being_repaid,
        user_id: user.id,
      })
      .select();
    if (error) { console.error('Error creating liability:', error); return { error }; }
    if (data && data[0] && liability.history) await syncHistory(DB.LIABILITIES_HISTORY, DB.LIABILITY_ID, data[0].id, liability.history);
    await fetchLiabilities();
    await fetchLiabilitiesHistory();
    return { data };
  };


  // UPDATE LIABILITY

  const updateLiability = async (id, updated_liability) => {
    const { data, error } = await supabase
      .from(DB.LIABILITIES)
      .update({
        name: updated_liability.name,
        description: updated_liability.description,
        valuation: updated_liability.valuation,
        ownership: updated_liability.ownership,
        classification: updated_liability.classification,
        include_networth: updated_liability.include_networth,
        interest_rate: updated_liability.interest_rate,
        term_years: updated_liability.term_years,
        term_months: updated_liability.term_months,
        being_repaid: updated_liability.being_repaid,
        updated: new Date().toISOString(),
        user_id: user.id,
      })
      .eq('id', id)
      .select();
    if (error) { console.error('Error updating liability:', error); return { error }; }
    if (updated_liability.history) await syncHistory(DB.LIABILITIES_HISTORY, DB.LIABILITY_ID, id, updated_liability.history);
    fetchLiabilities().then(() => fetchLiabilitiesHistory());
    return { data };
  };


  // DELETE LIABILITY

  const deleteLiability = async (id) => {
    await supabase.from(DB.LIABILITIES_HISTORY).delete().eq(DB.LIABILITY_ID, id).eq('user_id', user.id);
    const { data, error } = await supabase.from(DB.LIABILITIES).delete().eq('id', id).eq('user_id', user.id).select();
    if (error) { console.error('Error deleting liability:', error); return { error }; }
    await fetchLiabilities();
    return { data };
  };


  // FETCH HISTORY (shared implementation)

  const fetchHistory = async (table, idColumn, setState, errorMsg) => {
    if (!user) return;
    const { data, error } = await supabase
      .from(table)
      .select(`${idColumn}, date, valuation`)
      .eq('user_id', user.id)
      .order('date', { ascending: false });
    if (error) { console.error(`Error fetching ${table}:`, error); setDataError(errorMsg); return; }
    if (data) {
      const historyById = {};
      data.forEach(record => {
        if (!historyById[record[idColumn]]) historyById[record[idColumn]] = [];
        historyById[record[idColumn]].push({ date: record.date, valuation: record.valuation });
      });
      setState(prev => prev.map(item => ({ ...item, history: historyById[item.id] || [] })));
    }
  };

  const fetchAssetsHistory      = () => fetchHistory(DB.ASSETS_HISTORY,      DB.ASSET_ID,      setAssets,      'Failed to load asset history. Please refresh the page.');
  const fetchLiabilitiesHistory = () => fetchHistory(DB.LIABILITIES_HISTORY, DB.LIABILITY_ID,  setLiabilities, 'Failed to load liability history. Please refresh the page.');


  // SYNC HISTORY (shared implementation)

  const syncHistory = async (table, idColumn, itemId, history) => {
    if (!user || !history) return;
    await supabase.from(table).delete().eq(idColumn, itemId).eq('user_id', user.id);
    if (history.length > 0) {
      const { error } = await supabase.from(table).insert(
        history.map(record => ({ [idColumn]: itemId, user_id: user.id, date: record.date, valuation: record.valuation }))
      );
      if (error) console.error(`Error syncing ${table}:`, error);
    }
  };


  // FETCH HISTORY FOR SINGLE ITEM (on-demand)

  const fetchHistoryForItem = async (id, type) => {
    if (!user) return [];
    const tableName = type === 'Assets' ? DB.ASSETS_HISTORY      : DB.LIABILITIES_HISTORY;
    const idColumn  = type === 'Assets' ? DB.ASSET_ID            : DB.LIABILITY_ID;
    const { data, error } = await supabase
      .from(tableName).select('date, valuation')
      .eq(idColumn, id).eq('user_id', user.id)
      .order('date', { ascending: false });
    if (error) { console.error(`Error fetching ${type} history:`, error); return []; }
    return data || [];
  };


  // ─── COMPUTED VALUES ────────────────────────────────────────────────────────

  // networthHistoric — unified timeline for the Timeline page
  const networthHistoric = useMemo(() => {
    const allItems = [
      ...assets
        .filter(a => a.include_networth && a.history?.length)
        .map(a => ({
          id: a.id,
          classification: a.classification || 'Other',
          ownership: a.ownership / 100,
          isAsset: true,
          sorted: [...a.history].sort((a, b) => a.date.localeCompare(b.date)),
        })),
      ...liabilities
        .filter(l => l.include_networth && l.history?.length)
        .map(l => ({
          id: l.id,
          classification: l.classification || 'Short Term Debt',
          ownership: l.ownership / 100,
          isAsset: false,
          sorted: [...l.history].sort((a, b) => a.date.localeCompare(b.date)),
        })),
    ];

    if (allItems.length === 0) return { all: [] };

    const computeSnapshot = (date, period) => {
      let assets_total = 0, liabilities_total = 0;
      const classes = {};
      allItems.forEach(({ id, classification, ownership, isAsset, sorted }) => {
        if (sorted[0].date > date) return;
        let val = 0;
        for (let i = sorted.length - 1; i >= 0; i--) {
          if (sorted[i].date <= date) { val = sorted[i].valuation; break; }
        }
        const adjusted = Math.round(Math.abs(val) * ownership);
        if (!classes[classification]) classes[classification] = { total: 0 };
        classes[classification][id] = adjusted;
        classes[classification].total += adjusted;
        if (isAsset) assets_total += adjusted;
        else liabilities_total += adjusted;
      });
      return { period, assets_total, liabilities_total, networth_total: assets_total - liabilities_total, classes };
    };

    const todayStr = new Date().toISOString().split('T')[0];

    // all: every unique history date + today, period = ISO date
    const dateSet = new Set();
    allItems.forEach(({ sorted }) => sorted.forEach(h => dateSet.add(h.date)));
    dateSet.add(todayStr);
    const all = [...dateSet].sort().map(date => computeSnapshot(date, date));

    return { all };
  }, [assets, liabilities]);


  // networthCurrent — snapshot of today for the Net Worth page
  const networthCurrent = useMemo(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const assetClassMap = {};
    let assetsTotal = 0, assetsCount = 0, assetsNeedsUpdate = 0;
    assets.forEach(asset => {
      const cls = asset.classification || 'Other';
      const adjusted = asset.valuation * (asset.ownership / 100);
      if (!assetClassMap[cls]) assetClassMap[cls] = { name: cls, count: 0, valuation: 0, items: [] };
      assetClassMap[cls].items.push(asset);
      assetClassMap[cls].count++;
      assetsCount++;
      if (asset.include_networth) { assetsTotal += adjusted; assetClassMap[cls].valuation += adjusted; }
      if (new Date(asset.updated) < oneWeekAgo) assetsNeedsUpdate++;
    });

    const liabilityClassMap = {};
    let liabilitiesTotal = 0, liabilitiesCount = 0, liabilitiesNeedsUpdate = 0;
    liabilities.forEach(liability => {
      const cls = liability.classification || 'Short Term Debt';
      const adjusted = Math.abs(liability.valuation) * (liability.ownership / 100);
      if (!liabilityClassMap[cls]) liabilityClassMap[cls] = { name: cls, count: 0, valuation: 0, items: [] };
      liabilityClassMap[cls].items.push(liability);
      liabilityClassMap[cls].count++;
      liabilitiesCount++;
      if (liability.include_networth) { liabilitiesTotal += adjusted; liabilityClassMap[cls].valuation += adjusted; }
      if (new Date(liability.updated) < oneWeekAgo) liabilitiesNeedsUpdate++;
    });

    const total = assetsTotal - liabilitiesTotal;

    const prevYear = String(new Date().getFullYear() - 1);
    const baseline = networthHistoric.all?.findLast(s => s.period <= `${prevYear}-12-31`);
    const pct = (gain, base) => base !== 0 ? Math.round((gain / Math.abs(base)) * 100) : null;

    const growthSnap = networthHistoric.all?.find(s => s.networth_total > 0);

    return {
      total,
      ytd:        baseline ? total - baseline.networth_total : null,
      ytdPercent: baseline ? pct(total - baseline.networth_total, baseline.networth_total) : null,
      growthStart: growthSnap ? { year: parseInt(growthSnap.period), total: growthSnap.networth_total } : null,
      isNew: assets.length === 0 && liabilities.length === 0,
      assets: {
        total:        assetsTotal,
        count:        assetsCount,
        needs_update: assetsNeedsUpdate,
        ytd:          baseline ? assetsTotal - baseline.assets_total : null,
        ytdPercent:   baseline ? pct(assetsTotal - baseline.assets_total, baseline.assets_total) : null,
        classes: ASSET_CLASSES.map(cls => assetClassMap[cls]).filter(Boolean),
      },
      liabilities: {
        total:        liabilitiesTotal,
        count:        liabilitiesCount,
        needs_update: liabilitiesNeedsUpdate,
        ytd:          baseline ? liabilitiesTotal - baseline.liabilities_total : null,
        ytdPercent:   baseline ? pct(liabilitiesTotal - baseline.liabilities_total, baseline.liabilities_total) : null,
        classes: LIABILITY_CLASSES.map(cls => liabilityClassMap[cls]).filter(Boolean),
      },
    };
  }, [assets, liabilities, networthHistoric]);


  // networthProjected — forecast timeline for the FutureWorth page
  const networthProjected = useMemo(() => {
    const x_count = 10;
    const intervals = years / x_count;
    const currentYear = new Date().getFullYear();

    // The full class breakdown at any point t years out. Pulled out of the timeline loop so the
    // target scan below can sample it yearly, not just at the chart's 11 points.
    const pointAt = (t) => {
      const point = {
        year: (t + currentYear).toString(),
        "Other": 0, "Savings": 0, "Investment": 0, "Pension": 0, "Property": 0,
        "Short Term Debt": 0, "Long Term Debt": 0, "Mortgage": 0,
        "Net Worth": 0,
      };

      assets.forEach(asset => {
        if (!asset.include_networth) return;
        const principal = asset.valuation * (asset.ownership / 100);
        const rate = (asset.annual_growth || 0) / 100;
        const contributions = asset.annual_contributions || 0;
        const fv = rate === 0
          ? principal + (contributions * t)
          : Math.round(principal * Math.pow(1 + rate, t) + contributions * ((Math.pow(1 + rate, t) - 1) / rate));
        point[asset.classification] += fv;
        point["Net Worth"] += fv;
      });

      liabilities.forEach(liability => {
        if (!liability.include_networth) return;
        const principal = Math.abs(liability.valuation) * (liability.ownership / 100);
        const interestRate = (liability.interest_rate || 0) / 100;
        const totalTermYears = (liability.term_years || 0) + ((liability.term_months || 0) / 12);
        let fv;
        // A balance with no term entered can't be amortised — hold it flat. Without the term
        // check, totalTermYears is 0, `t >= totalTermYears` is true at t = 0, and the debt
        // disappears from the forecast entirely.
        if (!liability.being_repaid || totalTermYears <= 0) {
          fv = principal;
        } else if (t >= totalTermYears) {
          fv = 0;
        } else if (interestRate === 0) {
          fv = principal * (1 - (t / totalTermYears));
        } else {
          const monthlyRate = interestRate / 12;
          const numPayments = totalTermYears * 12;
          const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
          const paymentsRemaining = numPayments - (t * 12);
          fv = paymentsRemaining <= 0 ? 0 : Math.round(monthlyPayment * (Math.pow(1 + monthlyRate, paymentsRemaining) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, paymentsRemaining)));
        }
        point[liability.classification] += Math.round(fv);
        point["Net Worth"] -= fv;
      });

      return point;
    };

    const timeline = Array.from({ length: x_count + 1 }, (_, index) => pointAt(index * intervals));

    // yearsToTargetNetworth — years until the projection first crosses the target net worth.
    // Always scanned to the longest horizon, not the selected one, so changing the year selector
    // doesn't change the answer. null when the target is never reached inside that horizon.
    const yearsToTargetNetworth = (() => {
      if (!targetNetworth) return null;

      let previous = pointAt(0)["Net Worth"];
      if (previous >= targetNetworth) return 0;          // already there

      const horizon = YEAR_OPTIONS[YEAR_OPTIONS.length - 1];
      for (let t = 1; t <= horizon; t++) {
        const value = pointAt(t)["Net Worth"];
        if (value >= targetNetworth) {
          // Interpolate inside the crossing year so the figure isn't rounded up a whole year
          return (t - 1) + (targetNetworth - previous) / (value - previous);
        }
        previous = value;
      }
      return null;                                        // not reached within the horizon
    })();

    return { timeline, total: timeline[timeline.length - 1]?.["Net Worth"] || 0, years, setYears, yearsToTargetNetworth };
  }, [assets, liabilities, years, targetNetworth]);


  // ─── CONTEXT VALUE ───────────────────────────────────────────────────────────

  const value = {
    assets,
    liabilities,
    loadingUserData,
    dataError,
    refreshData: fetchAllData,
    // Asset operations
    createAsset,
    updateAsset,
    deleteAsset,
    // Liability operations
    createLiability,
    updateLiability,
    deleteLiability,
    // Computed — by page
    networthHistoric,
    networthCurrent,
    networthProjected,
    // History utilities
    fetchAssetsHistory,
    fetchLiabilitiesHistory,
    fetchHistoryForItem,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};


export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within DataProvider');
  return context;
};
